<template>
    <div class="flex flex-wrap box justify-between gap-y-3 gap-x-8 xl:gap-x-12">

        <div class="min-w-0">
            <div class="flex mb-2">
                <jet-label for="customer" value="Customer:" class="w-32 xl:w-20 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <multiselect class="multi-lg h-7 text-base"
                        v-model="form.customer_id"
                        :options="displayedCustomers"
                        valueProp="id"
                        trackBy="name"
                        label="name"
                        :searchable="true"
                        placeholder="Select Customer"
                        ref="customer"
                    />
                    <jet-input-error :message="$page.props.errors.customer_id" class="m1" />
                </div>
            </div>

            <template v-if="selectedCustomer">
                <div class="flex mb-2">
                    <jet-label for="address" value="Address:" class="w-32 xl:w-20 align-top leading-7" />
                    <div v-if="selectedCustomer" class="flex flex-1 flex-col">
                        <div>{{ selectedCustomer.address_1 }}</div>
                        <div>{{ selectedCustomer.address_2 }}</div>
                        <div>{{ selectedCustomer.city }} {{ selectedCustomer.province }}, {{ selectedCustomer.postal_code }}</div>
                    </div>
                </div>
                <div class="flex mb-2">
                    <jet-label for="phone" value="Phone:" class="w-32 xl:w-20 align-top leading-7" />
                    <div v-if="selectedCustomer">
                        {{ selectedCustomer.phone }}
                    </div>
                </div>
                <div class="flex items-start" v-if="selectedCustomer.emails.length">
                    <jet-label for="email" value="Email:" class="w-32 xl:w-20 align-top leading-7" />
                    <div class="flex flex-1">
                        <ul class="leading-tight">
                            <li v-for="email in selectedCustomer.emails" :key="email" class="break-all">{{ email }}</li>
                        </ul>
                    </div>
                </div>
            </template>
        </div>

        <div>
            <div class="flex mb-2">
                <jet-label for="order_num" value="Order #:" class="w-32 align-top leading-7" />
                <span>{{ form.id }}</span>
            </div>

            <div class="flex mb-2">
                <jet-label for="order_date" value="Order Date:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <datepicker class="w-100" inputClassName="dp-custom-input"
                        v-model="form.order_date"
                        weekStart="0"
                        monthNameFormat="long"
                        :enableTimePicker="false"
                        :autoApply="true"
                        :is24="false"
                        :clearable="false"
                        :maxDate="maxDate"
                        :format="formatDate"
                        position="right"
                        disabled
                    />
                    <jet-input-error :message="$page.props.errors.order_date" class="m-1" />
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="shipping_method" value="Shipping Method:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                   <multiselect class="multi-lg h-7 text-base"
                        v-model="form.shipping_method"
                        :options="shippingOptions"
                        valueProp="value"
                        trackBy="value"
                        label="label"
                        :canClear="false"
                        :canDeselect="false"
                        @select="checkDeliveryDays"
                    />
                    <jet-input-error :message="$page.props.errors.shipping_method" class="m1" />
                </div>
            </div>

            <div class="flex">
                <jet-label for="ship_date" value="Ship Date:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <datepicker class="w-100" inputClassName="dp-custom-input"
                        v-model="form.ship_date"
                        weekStart="0"
                        monthNameFormat="long"
                        :enableTimePicker="false"
                        :autoApply="true"
                        :is24="false"
                        :clearable="false"
                        :minDate="minDate"
                        :maxDate="maxDate"
                        :disabled-dates="customerDisabledDates"
                        :disabledWeekDays="[0]"
                        :format="formatDate"
                        position="right"
                    />
                    <jet-input-error :message="$page.props.errors.ship_date" class="m-1" />

                    <div v-if="orderWillBeSplit && minSpecialOrderDate">
                        <p class="text-red font-bold text-xs mt-1">Special Order/Dairy Items will be
                            {{ form.shipping_method === 'pickup' ? 'ready for pickup' : 'delivered' }}
                            on {{ minSpecialOrderDate.toFormat('yyyy-LL-dd') }}
                        </p>
                    </div>

                    <div class="flex justify-between pt-3 xl:pt-1">
                        <jet-label for="override_dates" value="Override Shipping Dates:" class="text-sm" />
                        <jet-input
                            type="checkbox"
                            v-model:checked="form.override_ship_date"
                            @click="toggleOverrideDates()"
                            class="h-5 w-5"
                            :title="form.id !== null ? 'Override cannot be changed after the order has been created.' : ''"
                        ></jet-input>
                    </div>
                </div>
            </div>
            
            <div class="flex mb-2" v-if="form.is_admin && selectedCustomerPayment">
                <jet-label for="payment_status" value="Payment Status:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                   <multiselect 
                        class="multi-lg h-7 text-base"
                        v-model="form.payment_status"
                        :options="paymentOptions"
                        :canClear="false"
                        :canDeselect="false"
                    />
                    <jet-input-error :message="$page.props.errors.payment_status" class="m1" />
                </div>
            </div>
        </div>

        <div>
            <div class="flex mb-2">
                <jet-label for="internal_notes" value="Internal Notes:" class="w-32 align-top leading-7" />
                <div v-if="selectedCustomer" class="flex flex-1">
                    <div>{{selectedCustomer.internal_notes}}</div>
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="notes" value="Shipping Notes:" class="w-32 align-top leading-7" />
                <div class="flex flex-1">
                    <textarea class="w-full resize-none" v-model="form.notes"></textarea>
                </div>
            </div>

            <div class="flex">
                <jet-label for="load_notes" value="Load Notes:" class="w-32 align-top leading-7" />
                <div v-if="selectedCustomer" class="flex flex-1">
                    <div>{{selectedCustomer.load_notes}}</div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import { DateTime } from 'luxon';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    components: {
        Datepicker,
        JetInput,
        JetInputError,
        JetLabel,
        Multiselect,
    },

    props: ['customers', 'categories', 'holidays', 'errors', 'form'],

    data() {
        return{
            displayedCustomers: this.customers.map( i => {
                return {
                    ...i,
                    disabled: !i.enabled
                }
            }),

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            paymentOptions: [
                { value: 'paid', label: 'Paid' },
                { value: 'unpaid', label: 'Unpaid' },
            ],
        }
    },

    methods: {
        checkDeliveryDays() {
            if(this.form.ship_date != null && this.form.shipping_method === 'delivery')
            {
                let day_selected = this.form.ship_date.toLocaleString('en-US', { weekday: 'long'});
                let deliveryDays = this.selectedCustomer.delivery_days;
                let isHoliday = this.holidays.includes(DateTime.fromJSDate(this.form.ship_date).toFormat('yyyy-LL-dd'));

                if (this.selectedCustomer.type === 'School') {
                    isHoliday = false;
                }

                if(isHoliday || !deliveryDays.toLowerCase().includes(day_selected.toLowerCase())) {
                    this.form.ship_date = null;
                }
            }
        },

        toggleOverrideDates() {
            this.form.override_ship_date = !this.form.override_ship_date;

            if(this.form.override_ship_date === false) {
                this.checkDeliveryDays();
            }

            return this.form.override_ship_date;
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        earliestPossibleShipDateForSchool() {
            let orderDate = DateTime.fromJSDate(this.form.order_date);

            let orderDayOfWeek = orderDate.toFormat('E');

            if (orderDayOfWeek > 3) { // Order day is after Wednesday
                return  orderDate.plus({weeks:2}).startOf('week');
            }
            return orderDate.plus({weeks:1}).startOf('week');
        }
    },

    computed: {
        orderWillBeSplit () {
            if (
                this.form.id !== null ||
                this.form.override_ship_date ||
                this.form.ship_date === null ||
                this.minSpecialOrderDate === null ||
                !(this.hasSpecialOrderItems ||this.hasDairyItems)
            ) {
                return false;
            }

            const shipDate = DateTime.fromJSDate(this.form.ship_date).startOf('day');
            const minSpecialOrderDate = this.minSpecialOrderDate.startOf('day');

            if (minSpecialOrderDate <= shipDate) {
                return false;
            }

            return true;
        },

        minSpecialOrderDate () {
            if (!this.selectedCustomer) {
                return null;
            }

            const deliveryDays = this.selectedCustomer.delivery_days;
            const maxAttempts = 14;
            let attempts = 0;
            let minDate = null;
            let today = DateTime.now();
            let minDaysOut = 2;
            let nextDate;

            if (today.weekday === 5) minDaysOut = 4;
            if (today.weekday === 6) minDaysOut = 3;

            nextDate = today.plus({ days: minDaysOut })

            if (!deliveryDays) {
                return nextDate;
            }

            while (minDate === null && attempts <= maxAttempts) {
                let checkDay = nextDate.toLocaleString({ weekday: 'long' });
                let isHoliday = this.holidays.includes(nextDate.toFormat('yyyy-LL-dd'));

                if (this.selectedCustomer.type === 'School') {
                    isHoliday = false;
                }

                if(!isHoliday && deliveryDays.toLowerCase().includes(checkDay.toLowerCase())) {
                    minDate = nextDate;
                }

                nextDate = nextDate.plus({ days:1 });
                attempts++;
            }

            if (minDate === null) {
                return nextDate;
            }

            return minDate;
        },

        minDate () {
            if (this.form.override_ship_date) {
                return null;
            }

            let today = DateTime.now();

            if ((this.hasSpecialOrderItems || this.hasDairyItems) && this.form.id !== null) {
                today = this.minSpecialOrderDate;

            } else if (this.form.shipping_method === 'delivery' && today.toLocaleString(DateTime.TIME_24_SIMPLE) >= '04:00') {
                today = today.plus({ days: 1 });
            }

            return today.toJSDate();
        },

        maxDate () {
            if (this.form.override_ship_date) {
                return null;
            }

            let today = new Date();
            return new Date(today.setMonth(today.getMonth() + 3));
        },

        selectedCustomer () {
            return this.form.customer_id == null ? null : this.customers.find(i => i.id == this.form.customer_id);
        },

        selectedCustomerPayment () {
            return this.selectedCustomer == null ? false : this.selectedCustomer.payment_method == 'credit_card' ? true : false;
        },

        customerDisabledDates () {
            if(this.selectedCustomer != null && !this.form.override_ship_date) {
                let deliveryDays = this.selectedCustomer.delivery_days;
                let disabledDays = [];
                let today = DateTime.now();
                let x = today.startOf('month');
                let endDate = today.plus({months:3}).endOf('month');
                let isDelivery = this.form.shipping_method == 'delivery';

                let earliestPossibleShipDateForSchool = this.earliestPossibleShipDateForSchool();

                while(x <= endDate) {

                    let isBeforeEarliestShipDay = false;
                    if (this.selectedCustomer.type === 'School' && x.diff(earliestPossibleShipDateForSchool, 'days').days < 0) {
                        isBeforeEarliestShipDay = true;
                    }

                    let checkDay = x.toLocaleString({ weekday: 'long'}).toLowerCase();
                    let isSunday = checkDay === 'sunday';
                    let deliveryNotAccepted = !deliveryDays.toLowerCase().includes(checkDay);
                    let isHoliday = this.holidays.includes(x.toFormat('yyyy-LL-dd'));

                    if(isHoliday) {
                        if (this.selectedCustomer.type === 'School') {
                            isHoliday = false;
                        }
                    }

                    if(isSunday || isHoliday || (isDelivery && deliveryNotAccepted) || isBeforeEarliestShipDay) {
                        disabledDays.push(new Date(x.toJSDate()));
                    }
                    x = x.plus({days:1});
                }

                return disabledDays;
            }

            return [];
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        hasSpecialOrderItems() {
            return this.form.order_items.filter(i => i.product.special_order === true).length > 0;
        },

        hasDairyItems() {
            if(!this.dairyCategory) return false;

            return this.form.order_items.filter(i => i.product.category_id === this.dairyCategory).length > 0;
        },
    },

    watch: {
        selectedCustomer(newVal, oldVal) {
            if(newVal != null)
            {
                // Prevent Inertia validation error from resetting the customer form
                let isSameCustomer = oldVal != null && oldVal.id === newVal.id;

                if(oldVal == null || !isSameCustomer) {
                    this.form.notes = newVal.order_notes;
                    this.form.shipping_method = newVal.default_shipping_method;
                    this.form.delivery_fee = newVal.delivery_fee;
                    this.checkDeliveryDays();
                }
            }
            else
            {
                this.form.ship_date = null;
                this.form.delivery_fee = null;
                this.form.notes = null;
                this.form.shipping_method = 'delivery';
            }
        }
    },

    mounted () {
        if (this.form.id === null) {
            this.$refs.customer.input.focus();
        }
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
.box>* {
    flex: 1 1 300px;
}
</style>

<style scoped>
input:active.disabled,
input:focus.disabled,
input:hover.disabled,
input.disabled {
    @apply bg-gray-300;
}
</style>
